import React from 'react';
import { LABEL, STRUCTURAL_TYPES } from '../../lib/formConstants';
import { normalizeShowWhen } from '../../lib/fieldConditions';
import { visibilityMode, DERIVABLE_FIELD_TYPES } from '../../lib/fieldTransforms';
import ShowWhenEditor from './ShowWhenEditor';
import DeriveEditor from './DeriveEditor';

const MODES = [
    ['always', 'Always visible'],
    ['conditional', 'Show when…'],
    ['hidden', 'Always hidden'],
];

/**
 * VisibilityEditor — one control for the three visibility modes, replacing the
 * bare "Show When" block in FieldInspector.
 *
 * The mode is DERIVED from `hidden` + `show_when`, never stored (see
 * fieldTransforms.visibilityMode). That is what makes this zero-risk for forms
 * already in production: an existing field with show_when classifies itself as
 * "Show when…" on first render, and one without classifies as "Always visible".
 * No migration, no backfill, no document rewritten.
 *
 * Props:
 *   field       — the selected field
 *   allFields   — flattened list of all form fields
 *   forwarders  — form forwarders (passed through to DeriveEditor)
 *   steps       — form steps (passed through to DeriveEditor)
 *   onUpdate    — FieldInspector's partial-merge callback
 */
export default function VisibilityEditor({ field, allFields = [], forwarders = [], steps = [], onUpdate }) {
    const mode = visibilityMode(field);
    const savedConditions = normalizeShowWhen(field.show_when)?.conditions?.length || 0;
    const canDerive = DERIVABLE_FIELD_TYPES.includes(field.field_type)
        && !STRUCTURAL_TYPES.includes(field.field_type);

    const setMode = (next) => {
        if (next === mode) return;
        if (next === 'hidden') {
            // show_when is deliberately PRESERVED so switching back restores the
            // author's rules. `required` is cleared because a hidden required
            // field can never be filled — the form would be unsubmittable.
            onUpdate({ hidden: true, required: false });
        } else if (next === 'always') {
            onUpdate({ hidden: false, show_when: null });
        } else {
            onUpdate({ hidden: false }); // ShowWhenEditor writes show_when
        }
    };

    return (
        <div className="space-y-2">
            <label className={LABEL}>Visibility</label>

            <div className="grid grid-cols-3 gap-1 rounded-lg bg-stone-100 p-0.5">
                {MODES.map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => setMode(value)}
                        aria-pressed={mode === value}
                        className={`rounded-md px-1.5 py-1.5 text-[10px] font-medium leading-tight transition-colors ${
                            mode === value
                                ? 'bg-white text-stone-900 shadow-sm'
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {mode === 'conditional' && (
                <ShowWhenEditor field={field} allFields={allFields} onUpdate={onUpdate} />
            )}

            {mode === 'hidden' && (
                <>
                    <p className="text-[10px] text-stone-500 leading-relaxed">
                        Never shown to the respondent and never required. Its value{' '}
                        <strong>is</strong> saved and sent to the CRM and to any forwarder
                        that maps it.
                    </p>

                    {savedConditions > 0 && (
                        <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 p-2">
                            <p className="text-[10px] text-amber-800 flex-1 leading-relaxed">
                                {savedConditions} “Show when” condition{savedConditions === 1 ? ' is' : 's are'}{' '}
                                saved on this field. They are paused while it is hidden, and
                                restored if you switch back.
                            </p>
                            <button
                                type="button"
                                onClick={() => onUpdate({ show_when: null })}
                                className="text-[10px] font-medium text-amber-700 hover:text-amber-900 flex-shrink-0"
                            >
                                Discard
                            </button>
                        </div>
                    )}

                    {canDerive ? (
                        <div>
                            <label className={LABEL}>Value</label>
                            <DeriveEditor
                                field={field}
                                allFields={allFields}
                                forwarders={forwarders}
                                steps={steps}
                                onUpdate={onUpdate}
                            />
                        </div>
                    ) : (
                        <p className="text-[10px] text-stone-400">
                            Derived values are available on text, long text, email, phone,
                            URL, and number fields. This field will submit its default value.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
